import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { MapPin, Eraser, FileCheck, Crosshair, Download } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fileViewUrl } from "@/lib/files";
import { formatDateTime } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

function SignaturePad({ canvasRef }) {
  const drawing = useRef(false);
  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d", { willReadFrequently: true });
    ctx.lineWidth = 2; ctx.lineCap = "round"; ctx.strokeStyle = "#1e293b";
    const pos = (e) => {
      const r = canvas.getBoundingClientRect();
      const t = e.touches ? e.touches[0] : e;
      return { x: (t.clientX - r.left) * (canvas.width / r.width), y: (t.clientY - r.top) * (canvas.height / r.height) };
    };
    const start = (e) => { drawing.current = true; const p = pos(e); ctx.beginPath(); ctx.moveTo(p.x, p.y); e.preventDefault(); };
    const move = (e) => { if (!drawing.current) return; const p = pos(e); ctx.lineTo(p.x, p.y); ctx.stroke(); e.preventDefault(); };
    const end = () => { drawing.current = false; };
    canvas.addEventListener("mousedown", start); canvas.addEventListener("mousemove", move);
    window.addEventListener("mouseup", end);
    canvas.addEventListener("touchstart", start); canvas.addEventListener("touchmove", move);
    window.addEventListener("touchend", end);
    return () => {
      canvas.removeEventListener("mousedown", start); canvas.removeEventListener("mousemove", move);
      window.removeEventListener("mouseup", end);
      canvas.removeEventListener("touchstart", start); canvas.removeEventListener("touchmove", move);
      window.removeEventListener("touchend", end);
    };
  }, [canvasRef]);
  return <canvas ref={canvasRef} width={460} height={160} className="w-full rounded-lg border border-slate-300 bg-white touch-none" data-testid="pod-signature-pad" />;
}

export function PodTab({ order, onUpdated }) {
  const { user } = useAuth();
  const canSubmit = ["admin", "support", "partner", "driver"].includes(user?.role) && !order.pod;
  const [form, setForm] = useState({ recipient_name: "", recipient_relation: "", recipient_document: "", location_address: "", note: "" });
  const [coords, setCoords] = useState({ lat: null, lng: null });
  const [saving, setSaving] = useState(false);
  const canvasRef = useRef(null);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const clearSig = () => {
    const c = canvasRef.current; c.getContext("2d").clearRect(0, 0, c.width, c.height);
  };
  const getLocation = () => {
    if (!navigator.geolocation) { toast.error("Geolocalizzazione non supportata"); return; }
    navigator.geolocation.getCurrentPosition(
      (p) => { setCoords({ lat: p.coords.latitude, lng: p.coords.longitude }); toast.success("Posizione GPS acquisita"); },
      () => toast.error("Impossibile ottenere la posizione GPS"),
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const submit = async () => {
    if (!form.recipient_name) { toast.error("Inserisci il nome del destinatario"); return; }
    const canvas = canvasRef.current;
    const blank = document.createElement("canvas");
    blank.width = canvas.width; blank.height = canvas.height;
    const hasSig = canvas.toDataURL() !== blank.toDataURL();
    setSaving(true);
    try {
      const payload = {
        ...form,
        location_lat: coords.lat, location_lng: coords.lng,
        signature: hasSig ? canvas.toDataURL("image/png") : null,
      };
      const { data } = await api.post(`/orders/${order.id}/pod`, payload);
      toast.success("Prova di consegna registrata");
      onUpdated?.(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  if (order.pod) {
    const pod = order.pod;
    return (
      <div className="max-w-2xl space-y-4" data-testid="pod-summary">
        <div className="flex items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800">
          <FileCheck size={18} className="text-emerald-600" /> Consegna confermata il {formatDateTime(pod.delivered_at)}
        </div>
        <div className="grid sm:grid-cols-2 gap-4 rounded-xl border border-slate-200 bg-white p-5 text-sm">
          <Info label="Destinatario" value={pod.recipient_name} />
          <Info label="Relazione" value={pod.recipient_relation} />
          <Info label="Documento" value={pod.recipient_document} />
          <Info label="Luogo" value={pod.location_address} />
          <Info label="Coordinate GPS" value={pod.location_lat != null ? `${pod.location_lat.toFixed(5)}, ${pod.location_lng.toFixed(5)}` : "—"} />
          <Info label="Registrato da" value={pod.delivered_by} />
        </div>
        {pod.note && <p className="text-sm text-slate-600">{pod.note}</p>}
        {pod.pdf_document_id && (
          <a href={fileViewUrl(pod.pdf_document_id)} target="_blank" rel="noreferrer">
            <Button variant="outline" data-testid="pod-download-pdf"><Download size={16} className="mr-1.5" /> Scarica PDF POD</Button>
          </a>
        )}
      </div>
    );
  }

  if (!canSubmit) return <p className="text-sm text-slate-400 py-6">Prova di consegna non ancora registrata.</p>;

  return (
    <div className="max-w-2xl space-y-5" data-testid="pod-form">
      <div className="grid sm:grid-cols-2 gap-4">
        <Field label="Nome destinatario *"><Input value={form.recipient_name} onChange={(e) => set("recipient_name", e.target.value)} data-testid="pod-recipient-name" /></Field>
        <Field label="Relazione col defunto"><Input value={form.recipient_relation} onChange={(e) => set("recipient_relation", e.target.value)} data-testid="pod-recipient-relation" /></Field>
        <Field label="Documento destinatario"><Input value={form.recipient_document} onChange={(e) => set("recipient_document", e.target.value)} data-testid="pod-recipient-doc" /></Field>
        <Field label="Indirizzo di consegna"><Input value={form.location_address} onChange={(e) => set("location_address", e.target.value)} data-testid="pod-address" /></Field>
      </div>

      <div className="flex items-center gap-3">
        <Button type="button" variant="outline" onClick={getLocation} data-testid="pod-gps-btn"><Crosshair size={16} className="mr-1.5" /> Acquisisci GPS</Button>
        <span className="text-xs text-slate-500 flex items-center gap-1" data-testid="pod-coords">
          <MapPin size={13} /> {coords.lat != null ? `${coords.lat.toFixed(5)}, ${coords.lng.toFixed(5)}` : "Posizione non acquisita"}
        </span>
      </div>

      <div>
        <div className="flex items-center justify-between mb-1.5">
          <Label>Firma del destinatario</Label>
          <button type="button" onClick={clearSig} className="text-xs text-slate-500 hover:text-slate-800 flex items-center gap-1" data-testid="pod-clear-sig"><Eraser size={13} /> Cancella</button>
        </div>
        <SignaturePad canvasRef={canvasRef} />
      </div>

      <Field label="Note"><Textarea value={form.note} onChange={(e) => set("note", e.target.value)} data-testid="pod-note" /></Field>

      <Button onClick={submit} disabled={saving} className="bg-blue-900 hover:bg-blue-800" data-testid="pod-submit-btn">
        <FileCheck size={16} className="mr-1.5" /> {saving ? "Registrazione…" : "Registra consegna"}
      </Button>
    </div>
  );
}

function Field({ label, children }) {
  return <div className="space-y-1.5"><Label>{label}</Label>{children}</div>;
}
function Info({ label, value }) {
  return <div><div className="text-xs uppercase tracking-wider text-slate-400">{label}</div><div className="text-slate-800 font-medium mt-0.5">{value || "—"}</div></div>;
}
